import { useTheme } from '@/hooks/use-theme.hook';
import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';
import { CARD_ELEVATION } from './page-card.styles';

type AppLayoutProps = {
  children: React.ReactNode;
  variant?: 'default' | 'full-bleed';
};

/**
 * Per-route content wrapper that handles layout variant (padding).
 *
 * The persistent shell (sidebars, overlays) lives in AppShell,
 * mounted once at the root - it never remounts on navigation.
 */
export const AppLayout = ({
  children,
  variant = 'default',
}: AppLayoutProps) => {
  return (
    <MainContent isFullBleed={variant === 'full-bleed'}>{children}</MainContent>
  );
};

function MainContent({
  children,
  isFullBleed,
}: {
  children: React.ReactNode;
  isFullBleed: boolean;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const shellRef = useRef<HTMLDivElement>(null);

  // Set CSS custom properties for drawer insets from where the page card
  // starts: the tabbed sidebar holds what a top bar would, so the card sits
  // under the shell's bars, inset 8px on every free side, and the fixed side
  // drawers line up with it.
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      document.documentElement.style.setProperty(
        '--drawer-inset-top',
        `${rect.top + 8}px`
      );
      document.documentElement.style.setProperty(
        '--drawer-inset-bottom',
        '8px'
      );
      document.documentElement.style.setProperty('--drawer-inset-right', '8px');
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    // ResizeObserver only sees this wrapper's own size. The shell-level bars
    // above it change the wrapper's viewport POSITION (rect.top), not
    // its size — the shell dispatches a resize event on bar height changes so
    // the inset re-measures.
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      ref={shellRef}
      data-page-shell='tabs'
      className={cn(
        // The margin snaps rather than transitions: animating it resized the
        // page every frame, and a full-width table re-laid out every cell per
        // frame. It follows `data-sidebar-collapsed` on <html>, so React does
        // not re-render the page as the sidebar folds.
        'flex min-h-0 min-w-0 flex-1 flex-col',
        // The whole window sits on the sidebar's tint.
        'ml-[256px] bg-[hsl(var(--sidebar-tint))]',
        // The page keeps this margin beside Clu's dock: the dock's frame
        // gives the nav's width back (see dockFrameClass), so the page
        // never moves as the dock opens or closes.
        'in-data-[sidebar-collapsed="true"]:ml-16',
        // No nav rail on a phone (see AppShell).
        'touch-only:ml-0!',
        // An agent's chat with no panel beside it (its guided screen, or the
        // panel put away) takes the whole width; the page steps aside.
        'in-data-[agent-panel="none"]:hidden'
      )}
    >
      <main
        className={cn(
          'app-scroll-region relative my-2 mr-2 min-h-0 flex-1 rounded-xl transition-colors duration-200',
          isDark ? 'bg-[#1E1E1E]' : 'bg-white',
          // The fixed sidebar paints over this card's left shadow, so the
          // sidebar draws that side itself (see TabsSidebar).
          CARD_ELEVATION,
          isFullBleed ? 'flex flex-col' : 'p-3 lg:p-4'
        )}
      >
        {children}
      </main>
    </div>
  );
}
